import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Widget {
  id: string
  type: string
  x: number
  y: number
  w: number
  h: number
  config: Record<string, any>
}

interface Report {
  id: string
  name: string
  description: string
  template_id?: string
  created_by: string
  created_at: string
  updated_at: string
  status: string
  layout: {
    columns: number
    rows: string
    widgets: Widget[]
  }
  settings: Record<string, any>
  schedule?: {
    enabled: boolean
    frequency: string
    recipients: string[]
  }
}

interface Template {
  id: string
  name: string
  description: string
  category: string
  thumbnail?: string
  layout: any
}

interface WidgetType {
  id: string
  name: string
  description: string
  category: string
  icon: string
  configurable: string[]
}

interface DataSource {
  id: string
  name: string
  type: string
  fields: string[]
}

const ReportBuilderPage: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [widgetTypes, setWidgetTypes] = useState<WidgetType[]>([])
  const [dataSources, setDataSources] = useState<DataSource[]>([])
  const [selectedReport, setSelectedReport] = useState<Report | null>(null)
  const [activeTab, setActiveTab] = useState<'reports' | 'templates' | 'builder'>('reports')
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showWidgetModal, setShowWidgetModal] = useState(false)
  const [selectedWidget, setSelectedWidget] = useState<Widget | null>(null)

  // Form states
  const [newReportName, setNewReportName] = useState('')
  const [newReportDescription, setNewReportDescription] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [widgetType, setWidgetType] = useState('')
  const [widgetConfig, setWidgetConfig] = useState<Record<string, any>>({})

  const API_BASE = 'http://192.168.0.98:8090/api'

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [reportsRes, templatesRes, widgetTypesRes, dataSourcesRes] = await Promise.all([
        fetch(`${API_BASE}/report-builder/reports`),
        fetch(`${API_BASE}/report-builder/templates`),
        fetch(`${API_BASE}/report-builder/widget-types`),
        fetch(`${API_BASE}/report-builder/data-sources`)
      ])

      if (reportsRes.ok) setReports(await reportsRes.json())
      if (templatesRes.ok) setTemplates(await templatesRes.json())
      if (widgetTypesRes.ok) setWidgetTypes(await widgetTypesRes.json())
      if (dataSourcesRes.ok) setDataSources(await dataSourcesRes.json())
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateReport = async () => {
    try {
      const response = await fetch(`${API_BASE}/report-builder/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newReportName,
          description: newReportDescription,
          template_id: selectedTemplate,
          created_by: 'user-1'
        })
      })

      if (response.ok) {
        const newReport = await response.json()
        setReports([newReport, ...reports])
        setSelectedReport(newReport)
        setActiveTab('builder')
        setShowCreateModal(false)
        setNewReportName('')
        setNewReportDescription('')
        setSelectedTemplate(null)
      }
    } catch (error) {
      console.error('Error creating report:', error)
    }
  }

  const handleAddWidget = async () => {
    if (!selectedReport || !widgetType) return

    try {
      const response = await fetch(`${API_BASE}/report-builder/reports/${selectedReport.id}/widgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          widget_type: widgetType,
          position: { x: 0, y: selectedReport.layout.widgets.length * 3, w: 4, h: 3 },
          config: widgetConfig
        })
      })

      if (response.ok) {
        const newWidget = await response.json()
        setSelectedReport({
          ...selectedReport,
          layout: {
            ...selectedReport.layout,
            widgets: [...selectedReport.layout.widgets, newWidget]
          }
        })
        setShowWidgetModal(false)
        setWidgetType('')
        setWidgetConfig({})
      }
    } catch (error) {
      console.error('Error adding widget:', error)
    }
  }

  const handleDeleteWidget = async (widgetId: string) => {
    if (!selectedReport || !confirm('Delete this widget?')) return

    try {
      const response = await fetch(`${API_BASE}/report-builder/reports/${selectedReport.id}/widgets/${widgetId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setSelectedReport({
          ...selectedReport,
          layout: {
            ...selectedReport.layout,
            widgets: selectedReport.layout.widgets.filter(w => w.id !== widgetId)
          }
        })
      }
    } catch (error) {
      console.error('Error deleting widget:', error)
    }
  }

  const handleSaveReport = async () => {
    if (!selectedReport) return

    try {
      const response = await fetch(`${API_BASE}/report-builder/reports/${selectedReport.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          layout: selectedReport.layout,
          settings: selectedReport.settings,
          status: 'draft'
        })
      })

      if (response.ok) {
        alert('Report saved successfully!')
        fetchData()
      }
    } catch (error) {
      console.error('Error saving report:', error)
    }
  }

  const handlePublishReport = async () => {
    if (!selectedReport) return

    try {
      const response = await fetch(`${API_BASE}/report-builder/reports/${selectedReport.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'published' })
      })

      if (response.ok) {
        alert('Report published!')
        fetchData()
      }
    } catch (error) {
      console.error('Error publishing report:', error)
    }
  }

  const handleExportReport = async (format: string) => {
    if (!selectedReport) return

    try {
      const response = await fetch(`${API_BASE}/report-builder/reports/${selectedReport.id}/export?format=${format}`, {
        method: 'POST'
      })

      if (response.ok) {
        const result = await response.json()
        alert(`Export complete! Download URL: ${result.download_url}`)
      }
    } catch (error) {
      console.error('Error exporting report:', error)
    }
  }

  const getWidgetIcon = (type: string) => {
    const icons: Record<string, JSX.Element> = {
      metric_card: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      line_chart: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      ),
      bar_chart: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      pie_chart: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
        </svg>
      ),
      data_table: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      map: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      progress_ring: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" strokeWidth={2} strokeDasharray="40 20" />
        </svg>
      ),
      text_block: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
        </svg>
      )
    }
    return icons[type] || icons.metric_card
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'archived': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Header />
      <main style={{ padding: '32px', maxWidth: '1600px', margin: '0 auto' }}>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Custom Report Builder</h1>
              <p className="text-gray-600 mt-1">Design and generate custom reports with drag-and-drop widgets</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Report
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {(['reports', 'templates', 'builder'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  disabled={tab === 'builder' && !selectedReport}
                  className={`px-6 py-3 text-sm font-medium border-b-2 capitalize ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } ${tab === 'builder' && !selectedReport ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <div className="grid grid-cols-3 gap-6">
              {reports.map(report => (
                <div
                  key={report.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:border-blue-300 transition-colors cursor-pointer"
                  onClick={() => { setSelectedReport(report); setActiveTab('builder'); }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">{report.name}</h3>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{report.description}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(report.status)}`}>
                      {report.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{report.layout.widgets.length} widgets</span>
                    <span>Updated {formatDate(report.updated_at)}</span>
                  </div>

                  {report.schedule?.enabled && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-blue-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Scheduled {report.schedule.frequency}
                    </div>
                  )}
                </div>
              ))}

              {reports.length === 0 && (
                <div className="col-span-3 text-center py-12 bg-white rounded-lg border border-gray-200">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Reports Yet</h3>
                  <p className="text-gray-500 mb-4">Create your first custom report to get started</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Create Report
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="grid grid-cols-4 gap-6">
              {templates.map(template => (
                <div
                  key={template.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:border-blue-300 transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedTemplate(template.id)
                    setNewReportName(`${template.name} - ${new Date().toLocaleDateString()}`)
                    setNewReportDescription(template.description)
                    setShowCreateModal(true)
                  }}
                >
                  <div className="h-32 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
                    <svg className="w-16 h-16 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{template.description}</p>
                    <span className="inline-block mt-2 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs capitalize">
                      {template.category}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Builder Tab */}
          {activeTab === 'builder' && selectedReport && (
            <div className="grid grid-cols-12 gap-6">
              {/* Widget Palette */}
              <div className="col-span-2">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-4">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">Widgets</h3>
                  </div>
                  <div className="p-2 space-y-1 max-h-96 overflow-y-auto">
                    {widgetTypes.map(wt => (
                      <button
                        key={wt.id}
                        onClick={() => {
                          setWidgetType(wt.id)
                          setWidgetConfig({ title: wt.name })
                          setShowWidgetModal(true)
                        }}
                        className="w-full p-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-left"
                      >
                        <div className="p-1.5 bg-blue-50 rounded text-blue-600">
                          {getWidgetIcon(wt.id)}
                        </div>
                        <span className="text-sm text-gray-700">{wt.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Canvas */}
              <div className="col-span-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h2 className="font-semibold text-gray-900">{selectedReport.name}</h2>
                      <p className="text-sm text-gray-500">{selectedReport.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveReport}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        Save Draft
                      </button>
                      <button
                        onClick={handlePublishReport}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Publish
                      </button>
                      <div className="relative group">
                        <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                          Export
                        </button>
                        <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <button onClick={() => handleExportReport('pdf')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">PDF</button>
                          <button onClick={() => handleExportReport('xlsx')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">Excel</button>
                          <button onClick={() => handleExportReport('pptx')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">PowerPoint</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 min-h-[500px] bg-gray-50">
                    {selectedReport.layout.widgets.length === 0 ? (
                      <div className="text-center py-20 border-2 border-dashed border-gray-300 rounded-lg">
                        <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <p className="text-gray-500">Add widgets from the palette to build your report</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-12 gap-4">
                        {selectedReport.layout.widgets.map(widget => (
                          <div
                            key={widget.id}
                            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 relative group"
                            style={{
                              gridColumn: `span ${widget.w}`,
                              minHeight: `${widget.h * 60}px`
                            }}
                          >
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                              <button
                                onClick={() => setSelectedWidget(widget)}
                                className="p-1 bg-gray-100 rounded hover:bg-gray-200"
                              >
                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteWidget(widget.id)}
                                className="p-1 bg-red-100 rounded hover:bg-red-200"
                              >
                                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>

                            <div className="flex items-center gap-2 mb-2">
                              <div className="p-1 bg-blue-50 rounded text-blue-600">
                                {getWidgetIcon(widget.type)}
                              </div>
                              <span className="font-medium text-gray-700">{widget.config.title || widget.type}</span>
                            </div>

                            {/* Widget Preview */}
                            <div className="h-full flex items-center justify-center text-gray-400 border border-dashed rounded">
                              {widget.type === 'metric_card' && (
                                <div className="text-center">
                                  <div className="text-3xl font-bold text-gray-700">£125,432</div>
                                  <div className="text-sm text-green-600">+12.5%</div>
                                </div>
                              )}
                              {widget.type === 'line_chart' && (
                                <svg className="w-full h-20" viewBox="0 0 100 40">
                                  <polyline points="5,30 20,25 35,28 50,15 65,20 80,10 95,18" fill="none" stroke="#4A90E2" strokeWidth="2"/>
                                </svg>
                              )}
                              {widget.type === 'bar_chart' && (
                                <div className="flex items-end gap-2 h-16">
                                  <div className="w-4 bg-blue-400" style={{height: '60%'}}></div>
                                  <div className="w-4 bg-blue-400" style={{height: '80%'}}></div>
                                  <div className="w-4 bg-blue-400" style={{height: '45%'}}></div>
                                  <div className="w-4 bg-blue-400" style={{height: '90%'}}></div>
                                </div>
                              )}
                              {widget.type === 'pie_chart' && (
                                <svg className="w-16 h-16" viewBox="0 0 42 42">
                                  <circle cx="21" cy="21" r="16" fill="none" stroke="#4A90E2" strokeWidth="8" strokeDasharray="60 40"/>
                                  <circle cx="21" cy="21" r="16" fill="none" stroke="#50C878" strokeWidth="8" strokeDasharray="25 75" strokeDashoffset="-60"/>
                                </svg>
                              )}
                              {widget.type === 'data_table' && (
                                <div className="w-full text-xs">
                                  <div className="bg-gray-100 px-2 py-1 flex">
                                    <span className="flex-1">Market</span>
                                    <span className="flex-1">Revenue</span>
                                  </div>
                                  <div className="px-2 py-1 flex border-b">
                                    <span className="flex-1">Germany</span>
                                    <span className="flex-1">£125k</span>
                                  </div>
                                </div>
                              )}
                              {widget.type === 'progress_ring' && (
                                <svg className="w-16 h-16" viewBox="0 0 42 42">
                                  <circle cx="21" cy="21" r="16" fill="none" stroke="#E5E7EB" strokeWidth="4"/>
                                  <circle cx="21" cy="21" r="16" fill="none" stroke="#4A90E2" strokeWidth="4" strokeDasharray="75 25" transform="rotate(-90 21 21)"/>
                                  <text x="21" y="24" textAnchor="middle" className="text-xs font-bold" fill="#374151">75%</text>
                                </svg>
                              )}
                              {!['metric_card', 'line_chart', 'bar_chart', 'pie_chart', 'data_table', 'progress_ring'].includes(widget.type) && (
                                <span>Widget Preview</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Properties Panel */}
              <div className="col-span-2">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-4">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">Properties</h3>
                  </div>
                  <div className="p-4 space-y-4">
                    {selectedWidget ? (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Widget Type</label>
                          <p className="text-sm text-gray-700 capitalize">{selectedWidget.type.replace(/_/g, ' ')}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                          <input
                            type="text"
                            value={selectedWidget.config.title || ''}
                            onChange={(e) => setSelectedWidget({...selectedWidget, config: {...selectedWidget.config, title: e.target.value}})}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Data Source</label>
                          <select className="w-full border rounded px-2 py-1 text-sm">
                            {dataSources.map(ds => (
                              <option key={ds.id} value={ds.id}>{ds.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Width</label>
                            <input type="number" value={selectedWidget.w} className="w-full border rounded px-2 py-1 text-sm" min={1} max={12} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Height</label>
                            <input type="number" value={selectedWidget.h} className="w-full border rounded px-2 py-1 text-sm" min={1} max={10} />
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedWidget(null)}
                          className="w-full px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Apply Changes
                        </button>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">Select a widget to edit its properties</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Report Modal */}
          {showCreateModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Report</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Report Name</label>
                    <input
                      type="text"
                      value={newReportName}
                      onChange={(e) => setNewReportName(e.target.value)}
                      placeholder="e.g., Q4 Executive Summary"
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={newReportDescription}
                      onChange={(e) => setNewReportDescription(e.target.value)}
                      placeholder="Brief description of this report"
                      rows={2}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Template (Optional)</label>
                    <select
                      value={selectedTemplate || ''}
                      onChange={(e) => setSelectedTemplate(e.target.value || null)}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Start from scratch</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => { setShowCreateModal(false); setSelectedTemplate(null); }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateReport}
                    disabled={!newReportName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Create Report
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Widget Modal */}
          {showWidgetModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Add Widget</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Widget Type</label>
                    <p className="text-gray-600 capitalize">{widgetType.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={widgetConfig.title || ''}
                      onChange={(e) => setWidgetConfig({...widgetConfig, title: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Source</label>
                    <select
                      value={widgetConfig.data_source || ''}
                      onChange={(e) => setWidgetConfig({...widgetConfig, data_source: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Select data source</option>
                      {dataSources.map(ds => (
                        <option key={ds.id} value={ds.id}>{ds.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => { setShowWidgetModal(false); setWidgetType(''); setWidgetConfig({}); }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddWidget}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add Widget
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default ReportBuilderPage
